/**
 * Folder-level variables — the single source for reading and writing them.
 *
 * Folders live in `collection.settings.folders[]`; a folder may carry
 * `variables: [{ key, value, enabled, secret?, scriptAccess?, options? }]`,
 * the same row shape as `settings.collection_variables`.
 *
 * Importers stored these rows for a long time without anything resolving
 * them, and without an `enabled` field (Postman dropped `disabled`, Bruno /
 * Hoppscotch filtered disabled rows out). Every resolver in the app tests
 * `item.enabled` for truthiness, so rows are normalised ON READ here: a
 * missing `enabled` means enabled. No data migration.
 *
 * Precedence (one order everywhere):
 *   script-local > session overlay > folder (nearest first → outermost)
 *   > environment > collection > workspace > global.
 *
 * Consumers only ever receive the FLAT, nearest-first list from
 * `folderVariablesFor` / `folderVariablesForTab`, so an existing
 * "first enabled match in a list" resolver gains exactly one list and
 * nearest-folder-wins falls out of `.find`.
 */
import { getFolders, normalizeParentId, withCollectionFolders } from './collectionFolders';
import {
  findParentCollectionId,
  findSidebarRequest,
  resolveRequestTabCollectionId,
} from './requestResolve';

/** Stable empty result, so React memo deps do not churn on "no folder vars". */
export const NO_FOLDER_VARS = Object.freeze([]);

/** Scope name used by every display / resolution site. */
export const FOLDER_SCOPE = 'folder';

/**
 * Canonical rows. Drops key-less rows; `enabled` defaults to true unless the
 * row says `enabled: false` or (Postman shape) `disabled: true`; `value` is a
 * string. Carries `secret` / `scriptAccess` / `options` through unchanged.
 */
export function normalizeFolderVariables(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const key = row.key == null ? '' : String(row.key);
    if (!key) continue;
    const next = {
      key,
      value: String(row.value ?? ''),
      enabled: !(row.enabled === false || row.disabled === true),
    };
    if (row.secret === true) next.secret = true;
    if (row.scriptAccess === true) next.scriptAccess = true;
    if (row.options !== undefined) next.options = row.options;
    out.push(next);
  }
  return out;
}

/**
 * The folders enclosing `folderId`, NEAREST FIRST, each with its normalised
 * rows. Walks `parent_id` the way `getFolderAuthChain` does: cycle-safe, and
 * stops at a folder that does not exist.
 */
export function getFolderVariableChain(collection, folderId) {
  const byId = new Map(getFolders(collection).map((f) => [String(f.id), f]));
  const chain = [];
  const seen = new Set();
  let current = normalizeParentId(folderId);
  while (current) {
    if (seen.has(current)) break;
    seen.add(current);
    const folder = byId.get(current);
    if (!folder) break;
    chain.push({
      folderId: String(folder.id),
      folderName: folder.name ?? '',
      variables: normalizeFolderVariables(folder.variables),
    });
    current = normalizeParentId(folder.parent_id);
  }
  return chain;
}

/**
 * Flat list, nearest folder's rows first, each a copy tagged with
 * `{ scope: 'folder', folderId, folderName, collectionId }` so a hover / panel
 * can say which folder a value came from, and a chip can open that folder's
 * editor without depending on its host knowing the collection (review N4).
 */
export function flattenFolderVariables(chain, collectionId = null) {
  const out = [];
  for (const link of chain || []) {
    for (const row of link.variables || []) {
      out.push({
        ...row,
        scope: FOLDER_SCOPE,
        folderId: link.folderId,
        folderName: link.folderName,
        collectionId: collectionId == null ? null : String(collectionId),
      });
    }
  }
  return out;
}

export function folderVariablesFor(collection, folderId) {
  if (!collection || folderId == null || folderId === '') return NO_FOLDER_VARS;
  const flat = flattenFolderVariables(getFolderVariableChain(collection, folderId), collection.id);
  return flat.length ? flat : NO_FOLDER_VARS;
}

/**
 * Folder variables for the request an editor tab shows. Mirrors
 * `requestFolderAuth.folderAuthChainForTab`: the SIDEBAR record is the source
 * of the folder (a request moved between folders keeps its tab, and a tab's
 * own `folderId` is only set on some open paths).
 */
export function folderVariablesForTab(tab, collections) {
  if (!tab) return NO_FOLDER_VARS;
  const collectionId = resolveRequestTabCollectionId(tab, collections);
  if (!collectionId) return NO_FOLDER_VARS;
  const collection = (collections || []).find((c) => String(c.id) === String(collectionId));
  if (!collection) return NO_FOLDER_VARS;
  const record = tab.originalId != null ? findSidebarRequest([collection], tab.originalId) : null;
  const folderId = record?.folder_id ?? tab.folderId ?? tab.folder_id ?? null;
  return folderId ? folderVariablesFor(collection, folderId) : NO_FOLDER_VARS;
}

/**
 * Folder variables for any request-shaped object (a tab, a sidebar record, a
 * merged draft), when the caller may already know its collection. The sidebar
 * record is still the folder source; `originalId` (tab) or `id` (record) finds it.
 */
export function folderVariablesForRequest(request, collections, collectionId = null) {
  if (!request) return NO_FOLDER_VARS;
  const requestId = request.originalId ?? request.id ?? null;
  const cid = collectionId
    ?? resolveRequestTabCollectionId(request, collections)
    ?? (requestId != null ? findParentCollectionId(collections, requestId) : null);
  if (cid == null) return NO_FOLDER_VARS;
  const collection = (collections || []).find((c) => String(c.id) === String(cid));
  if (!collection) return NO_FOLDER_VARS;
  const record = requestId != null ? findSidebarRequest([collection], requestId) : null;
  const folderId = record?.folder_id ?? request.folderId ?? request.folder_id ?? null;
  return folderId ? folderVariablesFor(collection, folderId) : NO_FOLDER_VARS;
}

/**
 * First enabled folder row for `name` (nearest folder wins, because the list
 * is nearest-first). `null` when none.
 */
export function findFolderVariable(folderVariables, name) {
  if (!Array.isArray(folderVariables)) return null;
  return folderVariables.find((v) => v?.enabled && v?.key === name) ?? null;
}

/**
 * The same flat list, OUTERMOST folder first — the shape "lowest priority
 * first" consumers want (script maps, codegen lists, where the last write
 * wins).
 */
export function folderVariablesLowestFirst(folderVariables) {
  if (!Array.isArray(folderVariables) || folderVariables.length === 0) return NO_FOLDER_VARS;
  return [...folderVariables].reverse();
}

/**
 * A new collection whose folder `folderId` has `variables` replaced. Every
 * other key of that folder (`auth`, scripts, name, parent, order) and every
 * other folder is kept as-is.
 */
export function withFolderVariables(collection, folderId, rows) {
  const variables = normalizeFolderVariables(rows);
  const folders = getFolders(collection).map((f) =>
    String(f.id) === String(folderId) ? { ...f, variables } : f
  );
  return withCollectionFolders(collection, folders);
}

/**
 * A folder page's Authorization fields: the folder's OWN rows (the live draft,
 * nearest) ahead of its ancestors'. A request inside the folder resolves that
 * auth with the folder's variables in scope, so they belong in the list —
 * unlike the folder's own Variables table, which uses ancestors only.
 */
export function withOwnFolderVariables(ownRows, ancestors, { collectionId, folderId, folderName } = {}) {
  const own = flattenFolderVariables(
    [{ folderId: String(folderId), folderName: folderName ?? '', variables: normalizeFolderVariables(ownRows) }],
    collectionId
  );
  const rest = Array.isArray(ancestors) ? ancestors : NO_FOLDER_VARS;
  return own.length ? [...own, ...rest] : rest;
}

/** Ancestors only (excludes `folderId` itself) — for chips inside the folder's own editor. */
export function ancestorFolderVariables(collection, folderId) {
  const chain = getFolderVariableChain(collection, folderId).slice(1);
  const flat = flattenFolderVariables(chain, collection?.id);
  return flat.length ? flat : NO_FOLDER_VARS;
}
